
import React, { useState } from "react"

import {
  TrashIcon,
  XCircleIcon,
  UserCircleIcon,
  EnvelopeIcon,
} from "@heroicons/react/24/outline"

import PulseLoader from "react-spinners/PulseLoader"
import moment from "moment-timezone"

import {
  useGetUserSchedulingSlotsQuery,
  useCreateNewUserSchedulingSlotMutation,
  useUpdateUserSchedulingSlotMutation,
  useDeleteUserSchedulingSlotMutation,
  useCreateCalendarEventForBookedSlotMutation,
} from "@/features/userSchedulingSlots/userSchedulingSlotsApiSlice"


const EtUserSchedulingSlotManager = ({
  schedulingPageId,
}) => {

  // First Created - 06 May 2026
  // Author - Atul Pandey
  // Purpose - Manage scheduling slots for a Trinity SlotBoard page.
  // Updated - 17 May 2026 - Google Calendar booking integration
  // Updated - 17 May 2026 - Timezone handling
  // Updated - 17 May 2026 - Automatic end time using duration
  // Updated - 27 Sep 2026 - Booked / Available / All filters
  // Updated - 27 Sep 2026 - Booked candidate details displayed immediately
  // Updated - 27 Sep 2026 - Removed nested scrolling and height restrictions


  /*
  |--------------------------------------------------------------------------
  | State
  |--------------------------------------------------------------------------
  */

  const [slotStart, setSlotStart] =
    useState("")

  const [slotEnd, setSlotEnd] =
    useState("")

  const [
    slotDurationMinutes,
    setSlotDurationMinutes,
  ] = useState(60)

  const [slotTimezone, setSlotTimezone] =
    useState("America/Los_Angeles")

  const [
    selectedStatusFilter,
    setSelectedStatusFilter,
  ] = useState("all")


  /*
  |--------------------------------------------------------------------------
  | RTK Query
  |--------------------------------------------------------------------------
  */

  const {
    data: userSchedulingSlotsData,
    isLoading,
    isError,
    error,
  } = useGetUserSchedulingSlotsQuery(
    { schedulingPageId },
    { skip: !schedulingPageId }
  )


  /*
  |--------------------------------------------------------------------------
  | Mutations
  |--------------------------------------------------------------------------
  */

  const [
    createNewUserSchedulingSlot,
    { isLoading: isCreating },
  ] = useCreateNewUserSchedulingSlotMutation()

  const [
    updateUserSchedulingSlot,
  ] = useUpdateUserSchedulingSlotMutation()

  const [
    deleteUserSchedulingSlot,
  ] = useDeleteUserSchedulingSlotMutation()

  const [
    createCalendarEventForBookedSlot,
    { isLoading: isCreatingCalendarEvent },
  ] = useCreateCalendarEventForBookedSlotMutation()


  /*
  |--------------------------------------------------------------------------
  | Normalize Slots
  |--------------------------------------------------------------------------
  */

  const userSchedulingSlots =
    userSchedulingSlotsData?.ids
      ?.map(
        id =>
          userSchedulingSlotsData?.entities?.[id]
      )
      ?.filter(Boolean) || []


  /*
  |--------------------------------------------------------------------------
  | Counts
  |--------------------------------------------------------------------------
  */

  const allCount =
    userSchedulingSlots.length

  const bookedCount =
    userSchedulingSlots.filter(
      slot => slot.slotStatus === "booked"
    ).length

  const availableCount =
    userSchedulingSlots.filter(
      slot => slot.slotStatus === "available"
    ).length


  /*
  |--------------------------------------------------------------------------
  | Filtered Slots
  |--------------------------------------------------------------------------
  */

  const filteredSlots =
    userSchedulingSlots
      .filter(slot => {

        if (selectedStatusFilter === "all") {
          return true
        }

        return slot.slotStatus === selectedStatusFilter

      })
      .slice()
      .sort((a, b) => {

        const aStart = a?.slotStart
          ? new Date(a.slotStart).getTime()
          : 0

        const bStart = b?.slotStart
          ? new Date(b.slotStart).getTime()
          : 0

        return aStart - bStart

      })


  /*
  |--------------------------------------------------------------------------
  | Filter Chips
  |--------------------------------------------------------------------------
  */

  const statusFilters = [
    {
      value: "all",
      label: "All",
      count: allCount,
    },
    {
      value: "booked",
      label: "Booked",
      count: bookedCount,
    },
    {
      value: "available",
      label: "Available",
      count: availableCount,
    },
  ]


  /*
  |--------------------------------------------------------------------------
  | Slot End Calculation
  |--------------------------------------------------------------------------
  */

  const calculateSlotEnd = (
    startValue,
    durationMinutes
  ) => {

    if (!startValue || !durationMinutes) {
      return ""
    }

    return moment(startValue)
      .add(Number(durationMinutes), "minutes")
      .format("YYYY-MM-DDTHH:mm")

  }


  const handleSlotStartChange = e => {

    const newStart = e.target.value

    setSlotStart(newStart)

    setSlotEnd(
      calculateSlotEnd(
        newStart,
        slotDurationMinutes
      )
    )

  }


  const handleSlotDurationChange = e => {

    const newDuration = Number(e.target.value)

    setSlotDurationMinutes(newDuration)

    if (slotStart) {

      setSlotEnd(
        calculateSlotEnd(
          slotStart,
          newDuration
        )
      )

    }

  }


  /*
  |--------------------------------------------------------------------------
  | Create Slot
  |--------------------------------------------------------------------------
  */

  const handleCreateSlot = async e => {

    e.preventDefault()

    if (
      !schedulingPageId ||
      !slotStart ||
      !slotEnd
    ) {
      return
    }

    try {

      await createNewUserSchedulingSlot({

        schedulingPageId,
        slotStart,
        slotEnd,
        slotTimezone,

      }).unwrap()

      setSlotStart("")
      setSlotEnd("")
      setSlotDurationMinutes(60)
      setSlotTimezone("America/Los_Angeles")

    } catch (error) {

      console.error(
        "Create scheduling slot error:",
        error
      )

    }

  }


  /*
  |--------------------------------------------------------------------------
  | Cancel Slot
  |--------------------------------------------------------------------------
  */

  const handleCancelSlot = async slotId => {

    try {

      await updateUserSchedulingSlot({

        slotId,
        slotStatus: "cancelled",

      }).unwrap()

    } catch (error) {

      console.error(
        "Cancel scheduling slot error:",
        error
      )

    }

  }


  /*
  |--------------------------------------------------------------------------
  | Delete Slot
  |--------------------------------------------------------------------------
  */

  const handleDeleteSlot = async slotId => {

    try {

      await deleteUserSchedulingSlot({
        slotId,
      }).unwrap()

    } catch (error) {

      console.error(
        "Delete scheduling slot error:",
        error
      )

    }

  }


  /*
  |--------------------------------------------------------------------------
  | Google Calendar Invite
  |--------------------------------------------------------------------------
  */

  const handleCreateCalendarInvite = async slotId => {

    try {

      await createCalendarEventForBookedSlot({
        slotId,
      }).unwrap()

    } catch (error) {

      console.error(
        "Create Calendar invite error:",
        error
      )

    }

  }


  /*
  |--------------------------------------------------------------------------
  | Format Date
  |--------------------------------------------------------------------------
  */

  const formatDateTime = (
    dateValue,
    timezone
  ) => {

    if (!dateValue) return ""

    return moment.utc(dateValue)
      .tz(
        timezone || "America/Los_Angeles"
      )
      .format("MMM D, YYYY, h:mm A")

  }


  /*
  |--------------------------------------------------------------------------
  | Render
  |--------------------------------------------------------------------------
  */

  return (

    <div
      className="
        w-full
        min-w-0
        bg-white
        rounded
        p-3
        font-poppins
      "
    >

      {/* Header */}

      <div className="mb-3">

        <p className="font-bold text-sm text-gray-800">
          SlotBoard Slots
        </p>

        <p className="text-[9px] text-gray-500 mt-1">
          Add available discussion slots and manage existing bookings.
        </p>

      </div>


      {/* Add Slot Form */}

      <form
        onSubmit={handleCreateSlot}
        className="bg-gray-100 rounded p-3 text-xs"
      >

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">

          <div className="min-w-0">

            <p className="text-[10px] font-bold mb-1">
              Start
            </p>

            <input
              type="datetime-local"
              value={slotStart}
              onChange={handleSlotStartChange}
              className="w-full min-w-0 rounded p-2"
            />

          </div>


          <div className="min-w-0">

            <p className="text-[10px] font-bold mb-1">
              Duration
            </p>

            <select
              value={slotDurationMinutes}
              onChange={handleSlotDurationChange}
              className="w-full rounded p-2"
            >

              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={60}>60 min</option>
              <option value={90}>1.5 hrs</option>
              <option value={120}>2 hrs</option>
              <option value={180}>3 hrs</option>
              <option value={240}>4 hrs</option>
              <option value={360}>6 hrs</option>
              <option value={480}>8 hrs</option>

            </select>

            <p className="text-[9px] text-gray-500 mt-1">
              Ends: {slotEnd || "Select start time"}
            </p>

          </div>


          <div className="min-w-0">

            <p className="text-[10px] font-bold mb-1">
              End
            </p>

            <input
              type="datetime-local"
              value={slotEnd}
              onChange={e => setSlotEnd(e.target.value)}
              className="w-full min-w-0 rounded p-2"
            />

          </div>


          <div className="min-w-0">

            <p className="text-[10px] font-bold mb-1">
              Timezone
            </p>

            <select
              value={slotTimezone}
              onChange={e => setSlotTimezone(e.target.value)}
              className="w-full rounded p-2"
            >

              <option value="America/Los_Angeles">US Pacific</option>
              <option value="America/Denver">US Mountain</option>
              <option value="America/Chicago">US Central</option>
              <option value="America/New_York">US Eastern</option>
              <option value="Europe/London">UK</option>
              <option value="Asia/Kolkata">India</option>

            </select>

          </div>

        </div>


        <div className="flex justify-end mt-3">

          <button
            type="submit"
            disabled={
              isCreating ||
              !slotStart ||
              !slotEnd
            }
            className="
              bg-orange-600
              hover:bg-orange-700
              text-white
              text-xs
              font-bold
              rounded
              px-4
              py-2
              disabled:opacity-40
            "
          >
            {isCreating ? "Adding..." : "Add Slot"}
          </button>

        </div>

      </form>


      {/* Existing Slots Header */}

      <div className="mt-4 border-b border-gray-200 pb-3">

        <div className="flex justify-between items-center gap-2">

          <p className="text-[10px] font-bold text-gray-700">
            Existing Slots
          </p>

          <p className="text-[9px] text-gray-500">
            {filteredSlots.length} shown
          </p>

        </div>


        {/* Filter Chips */}

        <div className="flex flex-wrap gap-2 mt-2">

          {statusFilters.map(filter => {

            const isSelected =
              selectedStatusFilter === filter.value

            return (

              <button
                key={filter.value}
                type="button"
                onClick={() =>
                  setSelectedStatusFilter(filter.value)
                }
                className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${
                  isSelected
                    ? "bg-orange-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-orange-50"
                }`}
              >
                {filter.label} ({filter.count})
              </button>

            )

          })}

        </div>

      </div>


      {/* Loading */}

      {isLoading && (
        <div className="p-3">
          <PulseLoader color="#ea580c" size={4} />
        </div>
      )}


      {/* Errors */}

      {isError && (

        <p className="text-xs text-orange-700 p-2">
          {error?.data?.message || "Unable to load slots."}
        </p>

      )}


      {/* Empty States */}

      {!isLoading &&
        userSchedulingSlots.length === 0 && (

          <div className="bg-gray-50 rounded p-4 mt-3">

            <p className="text-xs text-gray-500">
              No slots added yet for this SlotBoard page.
            </p>

          </div>

        )}


      {!isLoading &&
        userSchedulingSlots.length > 0 &&
        filteredSlots.length === 0 && (

          <div className="bg-gray-50 rounded p-4 mt-3">

            <p className="text-xs text-gray-500">
              No {selectedStatusFilter} slots found.
            </p>

          </div>

        )}


      {/*
      |--------------------------------------------------------------------------
      | Slot Cards
      |--------------------------------------------------------------------------
      |
      | No h-full, flex-1, max-h or nested overflow container.
      |
      | The parent Slots tab owns vertical scrolling.
      |
      */}

      <div className="mt-3 space-y-2">

        {filteredSlots.map((slot, index) => (

          <div
            key={slot._id}
            className="
              bg-gray-50
              border
              border-gray-200
              rounded
              p-3
              w-full
              min-w-0
            "
          >

            {/* Slot Header */}

            <div className="flex flex-wrap justify-between items-start gap-2">

              <div className="min-w-0 flex-1">

                <div className="flex flex-wrap items-center gap-2">

                  <p className="text-xs font-bold text-gray-800">

                    {index + 1}.{" "}

                    {formatDateTime(
                      slot.slotStart,
                      slot.slotTimezone
                    )}

                    {" to "}

                    {formatDateTime(
                      slot.slotEnd,
                      slot.slotTimezone
                    )}

                  </p>

                  <span
                    className={`rounded-full px-2 py-1 text-[9px] font-bold ${
                      slot.slotStatus === "booked"
                        ? "bg-orange-100 text-orange-700"
                        : slot.slotStatus === "available"
                          ? "bg-lime-100 text-lime-700"
                          : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {slot.slotStatus}
                  </span>

                </div>


                {/*
                |--------------------------------------------------------------------------
                | Booked Candidate Identity
                |--------------------------------------------------------------------------
                |
                | Immediately below slot date, not inside a lower card.
                |
                */}

                {slot.slotStatus === "booked" && (

                  <div
                    className="
                      bg-orange-50
                      border
                      border-orange-100
                      rounded
                      px-3
                      py-2
                      mt-2
                      w-full
                      min-w-0
                    "
                  >

                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">

                      {/* Name */}

                      <div className="flex items-center gap-2 min-w-0">

                        <UserCircleIcon
                          className="h-4 w-4 shrink-0 text-orange-600"
                        />

                        <div className="min-w-0">

                          <p className="text-[9px] text-orange-700 font-bold">
                            Booked by
                          </p>

                          <p className="text-xs font-bold text-gray-800 break-words">
                            {slot.bookedByName || "Name unavailable"}
                          </p>

                        </div>

                      </div>


                      {/* Email */}

                      <div className="flex items-center gap-2 min-w-0">

                        <EnvelopeIcon
                          className="h-4 w-4 shrink-0 text-orange-600"
                        />

                        <div className="min-w-0">

                          <p className="text-[9px] text-orange-700 font-bold">
                            Email
                          </p>

                          <p className="text-[11px] text-gray-800 break-all">
                            {slot.bookedByEmail || "Email unavailable"}
                          </p>

                        </div>

                      </div>

                    </div>

                  </div>

                )}


                {/* Timezone */}

                <p className="text-[9px] text-gray-500 mt-2">
                  Timezone: {slot.slotTimezone || "America/Los_Angeles"}
                </p>


                {/* Calendar Status */}

                {slot.slotStatus === "booked" && (

                  <div className="mt-2">

                    {slot.bookingGoogleCalendarEventId ? (

                      <p className="text-[10px] font-bold text-lime-700">
                        Calendar invite created
                      </p>

                    ) : (

                      <button
                        type="button"
                        disabled={isCreatingCalendarEvent}
                        onClick={() =>
                          handleCreateCalendarInvite(slot._id)
                        }
                        className="
                          bg-orange-600
                          hover:bg-orange-700
                          text-white
                          text-[10px]
                          font-bold
                          rounded
                          px-3
                          py-1.5
                          disabled:opacity-40
                        "
                      >
                        {isCreatingCalendarEvent
                          ? "Creating invite..."
                          : "Create Calendar Invite"}
                      </button>

                    )}

                  </div>

                )}

              </div>


              {/* Slot Actions */}

              <div className="flex items-center shrink-0">

                {slot.slotStatus !== "booked" &&
                  slot.slotStatus !== "cancelled" && (

                    <button
                      type="button"
                      onClick={() =>
                        handleCancelSlot(slot._id)
                      }
                      title="Cancel Slot"
                      className="p-1"
                    >
                      <XCircleIcon className="h-5 w-5 text-orange-600" />
                    </button>

                  )}


                {slot.slotStatus !== "booked" && (

                  <button
                    type="button"
                    onClick={() =>
                      handleDeleteSlot(slot._id)
                    }
                    title="Delete Slot"
                    className="p-1"
                  >
                    <TrashIcon className="h-5 w-5 text-orange-600" />
                  </button>

                )}

              </div>

            </div>

          </div>

        ))}

      </div>

    </div>

  )

}


export default EtUserSchedulingSlotManager